import { useEffect, useRef, useState } from 'react';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import axios from 'axios';

import { ErrorPage } from '../../../ErrorPage';
import { CardsLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { DisclaimerBox } from '../components/DisclaimerBox';
import { DataCard } from '../components/DataCard';
import { Callout } from '../components/Callout';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PrivatePayrollAuctionDocumentViewer } from '../../PrivatePayrollAuction/components/DocumentViewer';
import * as S from './styles';

interface BenefitCardsProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}


const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value: number, period: 'mês' | 'ano' = 'mês') =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + `% ao ${period}`;

const CardInformationScene = ({
    accentColor,
    clientThemeSettings,
    limit,
    registrationNumber,
    employmentBond,
    monthlyInterest,
    annualInterest,
    monthlyCet,
    annualCet,
    invoiceClosingDay,
    invoiceDueDay,
    remainingNetIncome,
    cardTermUrl,
    informedConsentTermUrl,
    onOpenDocument,
    onAccept,
}: {
    accentColor: string;
    clientThemeSettings: any;
    limit: number;
    registrationNumber: string;
    employmentBond: string;
    monthlyInterest: number;
    annualInterest: number;
    monthlyCet: number;
    annualCet: number;
    invoiceClosingDay: number;
    invoiceDueDay: number;
    remainingNetIncome: number;
    cardTermUrl: string;
    informedConsentTermUrl: string;
    onOpenDocument: (url: string, title: string) => void;
    onAccept: () => void;
}) => {
    const [confirmedTerms, setConfirmedTerms] = useState(false);
    const [confirmedDigitalInvoice, setConfirmedDigitalInvoice] = useState(false);
    const [confirmedConsentTerm, setConfirmedConsentTerm] = useState(false);
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom();
    const checkboxModal = useAnimatedModal();

    const allChecked =
        confirmedTerms &&
        confirmedDigitalInvoice &&
        (!informedConsentTermUrl || confirmedConsentTerm);
    const handleOpenTerm = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onOpenDocument(cardTermUrl, 'Termo de contratação do cartão benefício');
    };

    const handleOpenInformedConsentTerm = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onOpenDocument(informedConsentTermUrl, 'Termo de consentimento esclarecido');
    };

    return (
    <>
        <S.Header>
            {clientThemeSettings?.logo_url && (
                <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
            )}
        </S.Header>

        <S.ScrollContent>
            <S.HeroSection>
                <S.Title $accentColor={accentColor}>
                    <strong>Informações da contratação</strong> do seu cartão benefício
                </S.Title>
            </S.HeroSection>

            <S.DataCardContainer>
                <DataCard.Root>
                    <DataCard.Row label="Matrícula" value={registrationNumber} color={accentColor} />
                    <DataCard.Row label="Vínculo" value={employmentBond} color={accentColor} />
                    <DataCard.Row label="Limite do cartão" value={formatCurrency(limit)} color={accentColor} />
                    <DataCard.Row
                        label="Juros do parcelamento"
                        value={<>{formatPercent(monthlyInterest)}<br />{formatPercent(annualInterest, 'ano')}</>}
                        color={accentColor}
                    />
                    <DataCard.Row
                        label="CET do parcelamento"
                        value={<>{formatPercent(monthlyCet)}<br />{formatPercent(annualCet, 'ano')}</>}
                        color={accentColor}
                    />
                    <DataCard.Row label="Fechamento da fatura" value={`Todo dia ${invoiceClosingDay} do mês`} color={accentColor} />
                    <DataCard.Row label="Vencimento da fatura" value={`Todo dia ${invoiceDueDay} do mês`} color={accentColor} />
                    <DataCard.Row label="Renda líquida restante" value={formatCurrency(remainingNetIncome)} color={accentColor} />
                </DataCard.Root>
            </S.DataCardContainer>

            <S.DataCardContainer style={{ marginTop: 12 }}>
                <DisclaimerBox mode="info">
                    Para mais informações, consulte o{' '}
                    <a href={cardTermUrl} onClick={handleOpenTerm}>
                        termo de contratação do cartão benefício
                    </a>.
                </DisclaimerBox>
            </S.DataCardContainer>

            <S.DataCardContainer style={{ marginTop: 12 }}>
                <DisclaimerBox mode="info">
                    O cartão será entregue em até <strong>5 dias úteis</strong> após a confirmação do
                    desconto na sua folha de pagamento.
                </DisclaimerBox>
            </S.DataCardContainer>

            <S.DataCardContainer style={{ marginTop: 12 }}>
                <DisclaimerBox mode="info">
                    Você pode desistir da contratação do cartão em até <strong>7 dias</strong> (direito de arrependimento).
                </DisclaimerBox>
            </S.DataCardContainer>

            <S.CheckboxWrapper>
                <CheckboxField
                    id="scene3-terms"
                    checked={confirmedTerms}
                    onChange={(e) => setConfirmedTerms(e.target.checked)}
                    accentColor={accentColor}
                >
                    <strong>Confirmo que li e entendi</strong> as condições de contratação do cartão
                    benefício consignado.
                </CheckboxField>
                <CheckboxField
                    id="scene3-digital-invoice"
                    checked={confirmedDigitalInvoice}
                    onChange={(e) => setConfirmedDigitalInvoice(e.target.checked)}
                    accentColor={accentColor}
                    style={{ marginTop: '8px' }}
                >
                    Aceito receber minha fatura digital através do aplicativo.
                </CheckboxField>
                {informedConsentTermUrl && (
                    <>
                        <S.Separator />
                        <CheckboxField
                            id="scene3-consent-term"
                            checked={confirmedConsentTerm}
                            onChange={(e) => setConfirmedConsentTerm(e.target.checked)}
                            accentColor={accentColor}
                            decorate={true}
                        >
                            <strong>Confirmo que li e concordo</strong> com todas as condições descritas no <a href={informedConsentTermUrl} onClick={handleOpenInformedConsentTerm}>termo de consentimento esclarecido do cartão benefício.</a>
                        </CheckboxField>
                    </>
                )}
            </S.CheckboxWrapper>
            <div ref={contentEndRef} />
        </S.ScrollContent>

        <S.FooterSection>
            <div
                onClick={!hasScrolledToEnd ? scrollToBottom : !allChecked ? () => { scrollToBottom(); checkboxModal.open(); } : undefined}
                style={{ width: '100%' }}
            >
                <Button
                    clientThemeSettings={clientThemeSettings}
                    disabled={!allChecked}
                    onClick={onAccept}
                >
                    Continuar
                </Button>
            </div>
            <PoweredByQITech />
        </S.FooterSection>

        {checkboxModal.show && (
            <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                    <S.ModalTitle $accentColor={accentColor}>Confirmações pendentes</S.ModalTitle>
                    <S.ModalText>
                        Para continuar, você precisa <strong>marcar todas as caixas de confirmação</strong> desta tela.
                    </S.ModalText>
                    <S.ModalButtonRow>
                        <Button clientThemeSettings={clientThemeSettings} onClick={checkboxModal.close}>
                            Entendi
                        </Button>
                    </S.ModalButtonRow>
                </S.ModalSheet>
            </S.ModalOverlay>
        )}
    </>
    );
};

const WithdrawInformationScene = ({
    accentColor,
    clientThemeSettings,
    isChecked,
    setIsChecked,
    depositAmount,
    remainingAmount,
    monthlyCet,
    annualCet,
    monthlyInterest,
    annualInterest,
    cardLimit,
    iof,
    totalAmount,
    numberOfInstallments,
    installmentAmount,
    withdrawalTermUrl,
    onOpenDocument,
    onAccept,
}: {
    accentColor: string;
    clientThemeSettings: any;
    isChecked: boolean;
    setIsChecked: (v: boolean) => void;
    depositAmount: number;
    remainingAmount: number;
    monthlyInterest: number;
    annualInterest: number;
    cardLimit: number;
    monthlyCet: number;
    annualCet: number;
    iof: number;
    totalAmount: number;
    numberOfInstallments: number;
    installmentAmount: number;
    withdrawalTermUrl: string;
    onOpenDocument: (url: string, title: string) => void;
    onAccept: () => void;
}) => {
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom();
    const checkboxModal = useAnimatedModal();

    const handleOpenWithdrawalTerm = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onOpenDocument(withdrawalTermUrl, 'Contrato do saque emergencial');
    };

    return (
    <>
        <S.Header>
            {clientThemeSettings?.logo_url && (
                <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
            )}
        </S.Header>

        <S.ScrollContent>
            <S.HeroSection>
                <S.Title $accentColor={accentColor}>
                    <strong>Saque emergencial</strong>
                </S.Title>
                <S.BodyText $accentColor={accentColor} style={{ marginTop: 8 }}>
                    Parte do valor do seu limite do cartão benefício consignado será depositada na sua
                    conta e descontada da sua folha de pagamento.
                </S.BodyText>
            </S.HeroSection>

            <S.Wrapper>
                <Callout
                    mode="info"
                    accentColor={resolveColor('primary', clientThemeSettings)}
                    title={formatCurrency(depositAmount)}
                    description="Serão depositados na sua conta."
                />
                <Callout
                    mode="default"
                    title={formatCurrency(remainingAmount)}
                    description="Disponível para compra no seu cartão."
                />

                <DataCard.Root>
                    <DataCard.Row label="Limite do cartão" value={formatCurrency(cardLimit)} color={accentColor} />
                    <DataCard.Row label="Valor do saque" value={formatCurrency(totalAmount)} color={accentColor} />
                    <DataCard.Row label="IOF" value={formatCurrency(iof)} color={accentColor} />
                    <DataCard.Row label="Valor a ser depositado" value={formatCurrency(depositAmount)} color={accentColor} />
                    <DataCard.Row label="Valor disponível para compras" value={formatCurrency(remainingAmount)} color={accentColor} />
                    <DataCard.Row
                        label="Juros"
                        value={<>{formatPercent(monthlyInterest)}<br />{formatPercent(annualInterest, 'ano')}</>}
                        color={accentColor}
                    />
                    <DataCard.Row
                        label="CET"
                        value={<>{formatPercent(monthlyCet)}<br />{formatPercent(annualCet, 'ano')}</>}
                        color={accentColor}
                    />
                    <DataCard.Row label="Quantidade de descontos" value={String(numberOfInstallments)} color={accentColor} />
                    <DataCard.Row label="Valor mensal descontado" value={formatCurrency(installmentAmount)} color={accentColor} />
                </DataCard.Root>

                <S.DisclaimerWrapper>
                    <DisclaimerBox mode="away" title="Atenção">
                        {formatCurrency(installmentAmount)} serão descontados da sua folha de pagamento
                        por {numberOfInstallments} meses.{'\n\n'}Certifique-se de que isso cabe no
                        seu orçamento mensal.
                    </DisclaimerBox>

                    <DisclaimerBox mode="info">
                        Para mais informações, consulte o{' '}
                        <a href={withdrawalTermUrl} onClick={handleOpenWithdrawalTerm}>
                            contrato referente ao saque
                        </a>.
                    </DisclaimerBox>
                </S.DisclaimerWrapper>
            </S.Wrapper>

            <S.CheckboxWrapper>
                <CheckboxField
                    id="scene4-terms"
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    accentColor={accentColor}
                >
                    <strong>Estou ciente</strong> de que o saque emergencial será descontado da minha
                    folha de pagamento.
                </CheckboxField>
            </S.CheckboxWrapper>
            <div ref={contentEndRef} />
        </S.ScrollContent>

        <S.FooterSection>
            <div
                onClick={!hasScrolledToEnd ? scrollToBottom : !isChecked ? () => { scrollToBottom(); checkboxModal.open(); } : undefined}
                style={{ width: '100%' }}
            >
                <Button
                    clientThemeSettings={clientThemeSettings}
                    disabled={!isChecked}
                    onClick={onAccept}
                >
                    Continuar
                </Button>
            </div>
            <PoweredByQITech />
        </S.FooterSection>

        {checkboxModal.show && (
            <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                    <S.ModalTitle $accentColor={accentColor}>Confirmação pendente</S.ModalTitle>
                    <S.ModalText>
                    Para continuar, você precisa <strong>marcar todas as caixas de confirmação</strong> desta tela.
                    </S.ModalText>
                    <S.ModalButtonRow>
                        <Button clientThemeSettings={clientThemeSettings} onClick={checkboxModal.close}>
                            Entendi
                        </Button>
                    </S.ModalButtonRow>
                </S.ModalSheet>
            </S.ModalOverlay>
        )}
    </>
    );
};

const TOTAL_SCENES = 2;

export const CardsBenefitCards = ({
    signData,
    setSignerStep,
    setSignData,
}: BenefitCardsProps) => {
    const { clientThemeSettings } = useThemeContext();
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(false);
    const [sceneIndex, setSceneIndex] = useState(0);
    const [checkboxState, setCheckboxState] = useState<Record<number, boolean>>({});
    const [viewerDoc, setViewerDoc] = useState<{ url: string; title: string } | null>(null);

    const scrollContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scrollContentRef.current?.scrollTo({ top: 0 });
    }, [sceneIndex]);

    const card = signData?.benefit_card ?? {};
    const withdrawal = card?.withdrawal ?? {};

    const handleComplete = async () => {
        const payload = {
            step: 'benefit_card',
            event_date: new Date().toISOString(),
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);

        try {
            const response = await axios.patch('/signer', payload, config);
            setSignData({ ...signData, ...response.data });
            setSignerStep(response.data.next_step);
        } catch (err: any) {
            setError({
                has_error: true,
                status: [401, 403, 409].includes(err.response?.status)
                    ? err.response.status
                    : 500,
            });
        } finally {
            setLoading(false);
        }
    };

    const openDocumentViewer = (url: string, title: string) => {
        setViewerDoc({ url, title });
    };

    const handleAccept = () => {
        if (sceneIndex < TOTAL_SCENES - 1) {
            setCheckboxState({});
            setSceneIndex(sceneIndex + 1);
        } else {
            handleComplete();
        }
    };

    const accentColor = resolveColor('text', clientThemeSettings) || '#0e3573';

    if (loading) {
        return <CardsLoading />;
    }

    const isChecked = !!checkboxState[sceneIndex];
    const setIsChecked = (v: boolean) =>
        setCheckboxState((prev) => ({ ...prev, [sceneIndex]: v }));

    const scenes = [
        <CardInformationScene
            key={0}
            accentColor={accentColor}
            clientThemeSettings={clientThemeSettings}
            limit={card.limit}
            registrationNumber={card.registration_number}
            employmentBond={card.employment_bond}
            monthlyInterest={card.monthly_interest}
            annualInterest={card.annual_interest}
            monthlyCet={card.monthly_cet}
            annualCet={card.annual_cet}
            invoiceClosingDay={card.invoice_closing_day}
            invoiceDueDay={card.invoice_due_day}
            remainingNetIncome={card.remaining_net_income}
            cardTermUrl={card.term_document_url}
            informedConsentTermUrl={card.informed_consent_term_document_url}
            onOpenDocument={openDocumentViewer}
            onAccept={handleAccept}
        />,
        <WithdrawInformationScene
            key={1}
            accentColor={accentColor}
            clientThemeSettings={clientThemeSettings}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            depositAmount={withdrawal.deposit_amount}
            remainingAmount={withdrawal.remaining_amount}
            monthlyInterest={withdrawal.monthly_interest}
            annualInterest={withdrawal.annual_interest}
            monthlyCet={withdrawal.monthly_cet}
            annualCet={withdrawal.annual_cet}
            cardLimit={card.limit}
            iof={withdrawal.iof}
            totalAmount={withdrawal.total_amount}
            numberOfInstallments={withdrawal.number_of_installments}
            installmentAmount={withdrawal.installment_amount}
            withdrawalTermUrl={withdrawal.term_document_url}
            onOpenDocument={openDocumentViewer}
            onAccept={handleAccept}
        />,
    ];

    return (
        <S.PageWrapper>
            {scenes[sceneIndex]}

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            {viewerDoc && (
                <S.ViewerOverlay>
                    <PrivatePayrollAuctionDocumentViewer
                        url={viewerDoc.url}
                        title={viewerDoc.title}
                        onBack={() => setViewerDoc(null)}
                    />
                </S.ViewerOverlay>
            )}
        </S.PageWrapper>
    );
};
